/**
 * @voltxampere/persistence — Storage type contracts (Sprint 21)
 *
 * Two object stores are housed in one IndexedDB database
 * (`vxa-storage`):
 *
 *  - `circuits`  — keyed by `id`, indexed by `updatedAt`. Holds
 *                  the full Circuit object plus metadata for the
 *                  AYARLAR "Veri" tab (size estimate, timestamps).
 *  - `recents`   — keyed by `slot` (0..N-1) FIFO ring, holds
 *                  most-recently-opened circuit ids.
 *
 * Sprint 22+ may add a third object store for asset blobs
 * (per ADR-0041 forward-compat note); the schema-version constant
 * lets us bump cleanly without breaking existing databases.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export const DEFAULT_RECENTS_SIZE = 8;
/**
 * Database identity constants. Sprint 22 C4 bumped `DB_VERSION` to
 * 2 to add the `commandRecents` store; the upgrade handler in
 * `db.ts` switches on the outgoing version to apply incremental
 * changes.
 */
export const DB_NAME = "vxa-storage";
export const DB_VERSION = 4;
export const STORE_CIRCUITS = "circuits";
export const STORE_RECENTS = "recents";
export const STORE_COMMAND_RECENTS = "commandRecents";
// Sprint 23 C1 — single-row settings store: theme, locale, AC/transient
// defaults, custom shortcuts, onboarding tour state, a11y preferences.
// All AYARLAR persistence shares one row so "Tümünü temizle" hits one
// place and Sprint 24+ field additions don't trigger DB migrations.
export const STORE_SETTINGS = "settings";
// Sprint 32-4 — per-row bookmarks store. Each row is a Bookmark (id,
// circuitId, t, note, createdAt). Indexed by `by_circuitId` so the
// "load this circuit's bookmarks" path doesn't scan the full store.
export const STORE_BOOKMARKS = "bookmarks";
export const INDEX_BOOKMARKS_CIRCUIT_ID = "by_circuitId";
export const INDEX_CIRCUITS_UPDATED_AT = "by_updatedAt";
/**
 * Sentinel key used by the `commandRecents` store. The store holds
 * a single row whose `ids` field is the ordered list of command
 * ids — newest at index 0. We model it as a single-row store
 * (rather than per-id rows) because the palette UI reads/writes
 * the whole list atomically.
 */
export const COMMAND_RECENTS_SENTINEL_KEY = "_singleton";
/** Default ring size for command palette recent commands (BLUEPRINT §14.4). */
export const DEFAULT_COMMAND_RECENTS_SIZE = 8;
/**
 * Sprint 23 C1 — settings store sentinel key. Single-row layout
 * (mirrors commandRecents pattern): the entire AYARLAR persistence
 * lives under one IDB row keyed by `_singleton`. Reads/writes are
 * atomic (whole-row update); the SettingsRecord shape can grow
 * forward without a DB migration as long as new fields stay
 * optional.
 */
export const SETTINGS_SENTINEL_KEY = "_singleton";
//# sourceMappingURL=types.js.map